import React, { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { publicAssetUrl } from "@/lib/api";
import { listPartnerBrands } from "@/lib/partners";
import { Link as LinkIcon } from "lucide-react";

/**
 * Optional Partner Link section. Used inside merchant + admin post forms.
 * Emits {partner_url, partner_brand_slug} via `onChange`. Filter brands by
 * post type (MOVIE brands go to movies, OFFER brands to offers).
 */
export const PartnerLinkSection = ({
    value = {},
    onChange,
    postType = "OFFER",
    testidPrefix = "partner",
}) => {
    const [brands, setBrands] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [urlError, setUrlError] = useState("");

    useEffect(() => {
        listPartnerBrands()
            .then((rows) => setBrands(rows || []))
            .catch(() => setBrands([]))
            .finally(() => setLoaded(true));
    }, []);

    const filtered = brands.filter(
        (b) => !b.categories_hint || b.categories_hint.includes(postType) || b.categories_hint.length === 0,
    );

    const currentSlug = value?.partner_brand_slug || "";
    const currentUrl = value?.partner_url || "";

    const setSlug = (slug) => {
        const nextSlug = slug === "__none__" ? "" : slug;
        onChange?.({ partner_url: currentUrl, partner_brand_slug: nextSlug });
    };
    const setUrl = (url) => {
        if (url && !/^https?:\/\//i.test(url.trim())) setUrlError("URL must start with http:// or https://");
        else setUrlError("");
        onChange?.({ partner_url: url, partner_brand_slug: currentSlug });
    };

    const brandMissingUrl = !!currentSlug && !currentUrl;

    return (
        <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`${testidPrefix}-section`}>
            <div className="mb-2 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                    <LinkIcon size={14} />
                </div>
                <div className="text-sm font-bold text-neutral-800">Partner link (optional)</div>
            </div>
            <p className="mb-3 text-[11px] text-neutral-500">
                Send customers to the partner's exact page (e.g. Amazon product, BookMyShow show page).
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
                <div>
                    <Label className="text-xs">Partner brand</Label>
                    <Select value={currentSlug || "__none__"} onValueChange={setSlug}>
                        <SelectTrigger data-testid={`${testidPrefix}-brand`}>
                            <SelectValue placeholder="No brand" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">No brand</SelectItem>
                            {loaded && filtered.map((b) => (
                                <SelectItem key={b.slug} value={b.slug}>
                                    <span className="inline-flex items-center gap-2">
                                        <img src={publicAssetUrl(b.logo_url)} alt="" className="h-4 w-4 rounded" />
                                        {b.name}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs">Partner URL</Label>
                    <Input
                        placeholder="https://example.com/product"
                        value={currentUrl}
                        onChange={(e) => setUrl(e.target.value)}
                        maxLength={500}
                        data-testid={`${testidPrefix}-url`}
                        className={brandMissingUrl || urlError ? "border-rose-400" : ""}
                    />
                    {(urlError || brandMissingUrl) && (
                        <p className="mt-1 text-[11px] font-medium text-rose-600" data-testid={`${testidPrefix}-error`}>
                            {urlError || "Partner URL is required when a brand is selected"}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartnerLinkSection;
